<script setup>
// Lead Workspace — Requirements tab. Edits the exact same requirement
// columns Nexceo::LeadsController#update already permits (property_type,
// bedrooms, budget_min/max, preferred_location, purpose, timeline,
// requirement_notes) — no duplicate requirement storage. Reference fields
// with no backing column today (decision maker, amenities preference, floor
// preference, min/max size sq.ft, possession as distinct from timeline) are
// intentionally not included rather than faked.
import { reactive, ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import leadsAPI from 'dashboard/api/nexceo/leads';
import { formatBudgetRange, formatDateTime, humanize } from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
});
const emit = defineEmits(['reload']);

const PROPERTY_TYPES = ['Apartment', 'Villa', 'Plot', 'Commercial', 'Other'];
const PURPOSES = ['investment', 'self_use', 'rental', 'unknown'];
const TIMELINES = ['immediate', '1_month', '3_months', '6_months', 'unknown'];

const form = reactive({
  property_type: '',
  bedrooms: '',
  budget_min: '',
  budget_max: '',
  preferred_location: '',
  purpose: '',
  timeline: '',
  requirement_notes: '',
});

function syncFromLead() {
  form.property_type = props.lead.property_type || '';
  form.bedrooms = props.lead.bedrooms || '';
  form.budget_min = props.lead.budget_min || '';
  form.budget_max = props.lead.budget_max || '';
  form.preferred_location = props.lead.preferred_location || '';
  form.purpose = props.lead.purpose || '';
  form.timeline = props.lead.timeline || '';
  form.requirement_notes = props.lead.requirement_notes || '';
}

watch(() => props.lead.id, syncFromLead, { immediate: true });

const isSaving = ref(false);

// Requirement Profile — real, computed completeness (not a fabricated
// progress value): each section is "done" only when its underlying field(s)
// actually have data.
const profileSections = computed(() => [
  { label: 'Property Type & Configuration', done: !!(form.property_type && form.bedrooms) },
  { label: 'Budget', done: !!(form.budget_min || form.budget_max) },
  { label: 'Location Preference', done: !!form.preferred_location },
  { label: 'Purpose & Timeline', done: !!(form.purpose && form.timeline) },
  { label: 'Additional Notes', done: !!form.requirement_notes },
]);
const completionPercent = computed(() => {
  const done = profileSections.value.filter(s => s.done).length;
  return Math.round((done / profileSections.value.length) * 100);
});

async function save() {
  isSaving.value = true;
  try {
    await leadsAPI.update(props.lead.id, { ...form });
    useAlert('Requirement details saved.');
    emit('reload');
  } catch {
    useAlert('Failed to save requirement details.');
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid grid-cols-1 xl:grid-cols-[220px_1fr_320px] gap-4 p-4">
    <!-- Requirement Profile -->
    <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Requirement Profile</h3>
      <p class="text-xs text-n-slate-8 mb-2">{{ completionPercent }}% Complete</p>
      <div class="h-1.5 rounded-full bg-n-alpha-2 overflow-hidden mb-3">
        <div
          class="h-full bg-green-500 transition-all"
          :style="{ width: `${completionPercent}%` }"
        />
      </div>
      <ul class="space-y-1.5">
        <li
          v-for="s in profileSections"
          :key="s.label"
          class="flex items-center gap-2 text-xs"
        >
          <span
            class="size-3.5 rounded-full flex items-center justify-center shrink-0"
            :class="s.done ? 'bg-green-500' : 'border border-n-weak'"
          >
            <span v-if="s.done" class="i-lucide-check size-2.5 text-white" />
          </span>
          <span :class="s.done ? 'text-n-slate-12' : 'text-n-slate-8'">{{ s.label }}</span>
        </li>
      </ul>
    </div>

    <!-- Property Details form -->
    <div class="rounded-xl border border-n-weak bg-n-background p-4">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Property Details</h3>
      <p class="text-xs text-n-slate-8 mb-4">Capture the customer's property requirements</p>

      <label class="block text-xs font-medium text-n-slate-9 mb-1.5">Property Type</label>
      <div class="grid grid-cols-5 gap-2 mb-4">
        <button
          v-for="pt in PROPERTY_TYPES"
          :key="pt"
          type="button"
          class="flex flex-col items-center gap-1 rounded-lg border p-2.5 text-xs font-medium transition-colors"
          :class="
            form.property_type === pt
              ? 'border-woot-500 bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400'
              : 'border-n-weak text-n-slate-11 hover:border-woot-300'
          "
          @click="form.property_type = pt"
        >
          {{ pt }}
        </button>
      </div>

      <div class="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Configuration</label>
          <input
            v-model="form.bedrooms"
            type="text"
            placeholder="e.g. 3 BHK"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Preferred Location</label>
          <input
            v-model="form.preferred_location"
            type="text"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Budget Min</label>
          <input
            v-model="form.budget_min"
            type="number"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Budget Max</label>
          <input
            v-model="form.budget_max"
            type="number"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Purpose</label>
          <select
            v-model="form.purpose"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500 capitalize"
          >
            <option value="">Select…</option>
            <option v-for="p in PURPOSES" :key="p" :value="p">{{ humanize(p) }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-9 mb-1">Timeline</label>
          <select
            v-model="form.timeline"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500 capitalize"
          >
            <option value="">Select…</option>
            <option v-for="tl in TIMELINES" :key="tl" :value="tl">{{ humanize(tl) }}</option>
          </select>
        </div>
      </div>

      <label class="block text-xs font-medium text-n-slate-9 mb-1">Requirement Notes</label>
      <textarea
        v-model="form.requirement_notes"
        rows="3"
        maxlength="1000"
        class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500 resize-none"
      />

      <div class="flex justify-end mt-4">
        <button
          :disabled="isSaving"
          class="px-4 py-2 text-sm font-medium bg-woot-500 text-white rounded-lg hover:bg-woot-600 disabled:opacity-50"
          @click="save"
        >
          {{ isSaving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <!-- Requirement Summary -->
    <div class="flex flex-col gap-4">
      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">Requirement Summary</h3>
        <dl class="space-y-2 text-sm">
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Property Type</dt>
            <dd class="text-n-slate-12">{{ lead.property_type || '—' }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Configuration</dt>
            <dd class="text-n-slate-12">{{ lead.bedrooms || '—' }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Budget</dt>
            <dd class="text-n-slate-12">{{ formatBudgetRange(lead) || '—' }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Location</dt>
            <dd class="text-n-slate-12">{{ lead.preferred_location || '—' }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Purpose</dt>
            <dd class="text-n-slate-12 capitalize">{{ humanize(lead.purpose) || '—' }}</dd>
          </div>
          <div class="flex justify-between">
            <dt class="text-n-slate-8">Timeline</dt>
            <dd class="text-n-slate-12 capitalize">{{ humanize(lead.timeline) || '—' }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="lead.preference_summary || lead.ai_summary" class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2 flex items-center gap-1.5">
          <span class="i-lucide-quote size-3.5 text-woot-500" /> AI Preference Summary
        </h3>
        <p class="text-sm text-n-slate-11 italic">
          {{ lead.preference_summary || lead.ai_summary }}
        </p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Next Step</h3>
        <p v-if="lead.next_follow_up_at" class="text-sm text-n-slate-12">
          Follow-up on {{ formatDateTime(lead.next_follow_up_at) }}
        </p>
        <p v-else class="text-sm text-n-slate-8">No next step scheduled.</p>
      </div>
    </div>
  </div>
</template>
